import { useEffect, useReducer, type ReactElement } from 'react';
import { Icon } from '@/components/atoms/icon';
import { Button } from '@/components/atoms/button';
import { cn } from '@/lib/cn';
import { formatHoursMinutesSeconds, elapsedSecondsSince } from '@/lib/format-time';

export type PracticeTimerProps = {
  startTime: Date;
  isRunning: boolean;
  onToggle: () => void;
  onFinish: () => void;
  className?: string;
};

export function PracticeTimer({
  startTime,
  isRunning,
  onToggle,
  onFinish,
  className,
}: PracticeTimerProps): ReactElement {
  // Bump a tick counter each second while running so the component re-renders
  // and re-reads Date.now(). The displayed elapsed time is derived from
  // startTime, so we never need to mirror it in state.
  const [, tick] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isRunning]);

  const elapsedSeconds = elapsedSecondsSince(startTime);

  return (
    <div className={cn('flex flex-col items-center gap-8', className)}>
      <div
        aria-label="Practice timer"
        role="timer"
        className={cn(
          'font-display text-[64px] font-semibold text-white',
          'leading-none tracking-tighter tabular-nums',
          isRunning && 'pulse-timer',
        )}
      >
        {formatHoursMinutesSeconds(elapsedSeconds)}
      </div>

      <div className="flex w-full max-w-xs flex-col gap-3">
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={onToggle}
          aria-label={isRunning ? 'Pause practice' : 'Resume practice'}
        >
          {isRunning ? (
            <>
              <Icon name="pause" size={20} aria-hidden />
              <span>Pause Practice</span>
            </>
          ) : (
            <>
              <Icon name="play" size={20} aria-hidden />
              <span>Resume Practice</span>
            </>
          )}
        </Button>

        <Button
          variant="secondary"
          size="lg"
          fullWidth
          onClick={onFinish}
          aria-label="Finish session"
        >
          <Icon name="x" size={20} aria-hidden />
          <span>Finish Session</span>
        </Button>
      </div>
    </div>
  );
}
